import type { BenefitPlan, BenefitPlanGroup } from "../types/models";
import type { Rate } from "../types";
import type { EnrollmentMeasure } from "./quotedEnrollmentReview";

/** How serious a rate's enrollment mismatch is: the group's total, or the one rate. */
export type EnrollmentFlagLevel = "group" | "rate";

/** What decides the flags on a plan's enrolled cells. */
export interface EnrollmentFlagState {
  /** The group's total differs from Current's by 9% or more. */
  readonly groupFlagged: boolean;
  /** The analyst still owes a decision on this plan. */
  readonly needsDecision: boolean;
}

export const NO_ENROLLMENT_FLAGS: EnrollmentFlagState = {
  groupFlagged: false,
  needsDecision: false,
};

/** The flag state of one plan, from the Quotes read's group and schedule fields. */
export const enrollmentFlagStateOf = (
  group: BenefitPlanGroup,
  plan: BenefitPlan,
): EnrollmentFlagState => ({
  groupFlagged: group.enrollmentGroupFlagged === true,
  needsDecision:
    plan.rateSchedule?.some(
      (schedule) => schedule.enrollmentNeedsDecision === true,
    ) ?? false,
});

/** Whether the carrier quoted the figure a cell of this measure shows. */
const quotedMeasure = (rate: Rate, measure: EnrollmentMeasure): boolean =>
  measure === "volume"
    ? rate.carrierVolume !== undefined
    : rate.carrierNumberOfLives !== undefined;

/**
 * The flag a rate's enrolled cell shows. A rate flags while the analyst still owes a
 * decision, and only on the cell that shows a figure the carrier quoted. The group's total
 * is the more serious mismatch, so it takes the level over the rate's own.
 */
export const enrollmentFlagLevel = (
  state: EnrollmentFlagState,
  rate: Rate,
  measure: EnrollmentMeasure,
): EnrollmentFlagLevel | undefined => {
  if (!state.needsDecision || !quotedMeasure(rate, measure)) {
    return undefined;
  }

  if (state.groupFlagged) {
    return "group";
  }

  return rate.enrollmentVarianceFlagged ? "rate" : undefined;
};
